import Command from '@ckeditor/ckeditor5-core/src/command';
import first from '@ckeditor/ckeditor5-utils/src/first';

import { CkeditorConstants, randomUUIDGenerator } from "../../src/utils";

const { helperAttributes: { TRACK_CHANGES_EDITING }, CONTENT_HARMONIZATION: {
  PCICORE_HASATTACHMENT,
  PCICORE_MEDIARESOURCE,
  FRBR_EMBODIMENT,
  FRBR_EXPRESSION,
  FRBR_PART_OF,
  FRBR_MANIFESTATION
}, localStorageItem: { baseHrefForLinkPlugin },
  htmlElement: { SPAN },
  PLUGINS: { TRACK_CHANGES },
  COMMANDS: { CMD_TRACK_CHANGES }
} = CkeditorConstants;

/**
 * NOTE 1 : Decode the image asynchronously and allow other content to be rendered before this completes.
 * NOTE 2 : Loading the image lazy Tells the user agent to hold loading the image until the image not on viewport.
 */

export default class InsertImageCommand extends Command {
  execute({ value }) {
    this.editor.fire('insertimage');
    this.isEnabled = false;
    const isTrackChangesEnabled = this.editor.commands.get(CMD_TRACK_CHANGES).value && this.editor.plugins.get(TRACK_CHANGES).isEnabled;
    this.editor.model.change(writer => {
      this.baseHrefForLinkPlugin = this.editor.baseHrefForLinkPlugin;
      for (const item of value.imageContent) {
        const selection = this.editor.model.document.selection;
        const schema = this.editor.model.schema;
        const imageType = this.determineImageTypeForInsertionAtSelection(schema, selection);
        let parentWrapper = writer.createElement('img', {
          'rel': `${PCICORE_HASATTACHMENT}`,
          'id': randomUUIDGenerator(),
          'src': item.src,
          'alt': item.alt || item.name,
          'typeof': `${PCICORE_MEDIARESOURCE} ${FRBR_MANIFESTATION}`,
          'class': 'ck-media-image',
          'data-node-name': item.name,
          'height': item.height,
          'width': item.width,
          'data-tenant-id': item.tenantId,
          'loading': 'lazy',
          'decoding': 'async'
        });
        const relSpan = writer.createElement(SPAN, {
          rel: FRBR_PART_OF,
          resource: this.baseHrefForLinkPlugin
        })
        const id = randomUUIDGenerator();
        const about = `${this.editor.baseHrefForLinkPlugin}#${id}`;
        if (imageType === 'imageInline') {
          const spanTag = writer.createElement('inlineimage', {
            id, about
          });
          writer.append(relSpan, spanTag);
          writer.append(parentWrapper, relSpan);
          this._addContentHarmonization(writer, spanTag, item);
          this.editor.model.insertContent(
            spanTag,
            selection
          );
        } else {
          const figureTag = writer.createElement('figureTag', {
            id, about,
            'class': 'media-class',
            alignment: 'center'
          });
          writer.append(relSpan, figureTag);
          writer.append(parentWrapper, relSpan);
          this._addContentHarmonization(writer, figureTag, item);
          writer.insert(figureTag, selection.getFirstPosition());
          if (isTrackChangesEnabled) {
            const range = writer.createRangeOn(figureTag);
            this.editor.plugins.get(TRACK_CHANGES_EDITING).markInsertion(range);
          }
        }
      }
    });
    this.editor.focus();
  }

  determineImageTypeForInsertionAtSelection(schema, selection) {
    const firstBlock = first(selection.getSelectedBlocks());

    // Insert a block image if the selection is not in/on block elements or it's on a block widget.
    if (!firstBlock || schema.isObject(firstBlock)) {
      return 'imageBlock';
    }

    // A block image should also be inserted into an empty block element
    // (that is not an empty list item so the list won't get split).
    if (firstBlock.isEmpty && firstBlock.name != 'listItem') {
      return 'imageBlock';
    }

    if (firstBlock._children._nodes[0].name === 'figureTag') {
      return 'imageBlock';
    }

    // Otherwise insert an inline image.
    return 'imageInline';
  }

  /**
   * this is private method to add content harmonization
   * @param {Writer} writer 
   * @param {HTMLElement} figureTag 
   * @param {{[key]:value }} item 
   */
  _addContentHarmonization(writer, figureTag, item) {
    const getElement = (props) => writer.createElement(SPAN, {
      ...props
    });
    let src = item.tenantId?.split('/');
    const tenantId = src?.pop();
    writer.append(getElement({
      about: `${src.join('/')}/${tenantId ? tenantId.concat('-1000') : ''}`,
      typeof: `${FRBR_EXPRESSION} ${PCICORE_MEDIARESOURCE}`,
      rel: FRBR_EMBODIMENT,
      resource: item.tenantId
    }), figureTag);
  }
}
